import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader, Search, Zap } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { testTemplate } from "@/integrations/templates/templateService";
import { useNotifications } from "@/contexts/NotificationContext";
import { RecordWithTemplate } from "@/hooks/useRecords";
import { Separator } from "@/components/ui/separator";
import { RecordCard } from "@/components/dashboard/RecordCard";
import { Database } from "@/integrations/supabase/types";
import ReactMarkdown from "react-markdown";
import { cleanMarkdownText } from "@/lib/markdown-utils";
import { useTranslation } from "react-i18next";

interface TemplateTestDialogProps {
  promptText: string;
  disabled?: boolean;
}

const TemplateTestDialog = ({ promptText, disabled }: TemplateTestDialogProps) => {
  const { t } = useTranslation();
  const [isSelectRecordOpen, setIsSelectRecordOpen] = useState(false);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<RecordWithTemplate | null>(null);
  const [testResult, setTestResult] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const { addNotification } = useNotifications();
  const isMobile = useIsMobile();

  // Получаем список записей пользователя
  const { data: records, isLoading: recordsLoading } = useQuery({
    queryKey: ["user-records-for-testing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("records")
        .select("*, templates(*)")
        .eq("processing_status", "completed")
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      return data.map((record) => ({
        id: record.id,
        name: record.name,
        status: record.status,
        processingStatus: record.processing_status,
        processing_status_desc: record.processing_status_desc,
        duration: record.duration,
        filePath: record.file_path,
        createdAt: record.created_at,
        userId: record.user_id,
        templateId: record.template_id,
        processingStartedAt: record.processing_started_at,
        processingFinishedAt: record.processing_finished_at,
        processingTime: record.processing_time,
        processingResults: record.processing_results,
        transcriptText: record.transcript_text,
        postProcessingText: record.post_processing_text,
        deletedAt: record.deleted_at,
        template: record.templates
          ? {
            id: record.templates.id,
            name: record.templates.name,
            description: record.templates.description,
            prompt: record.templates.prompt,
            isSystem: record.templates.is_system,
            createdAt: record.templates.created_at,
            userId: record.templates.user_id,
          }
          : null,
      })) as RecordWithTemplate[];
    },
    enabled: isSelectRecordOpen,
  });

  // Фильтруем записи по поисковому запросу
  const filteredRecords =
    records?.filter((record) => record.name.toLowerCase().includes(searchQuery.toLowerCase())) || [];

  // Мутация для тестирования шаблона
  const testMutation = useMutation({
    mutationFn: async ({ recordId, prompt }: { recordId: string; prompt: string }) => {
      return await testTemplate(recordId, prompt);
    },
    onSuccess: (result) => {
      setTestResult(result);
    },
    onError: (error) => {
      console.error("Template test error:", error);
      addNotification({
        userId: "user",
        type: "error",
        message: t('templates.test.error_test'),
      });
    },
  });

  const handleRecordSelect = (record: RecordWithTemplate) => {
    setSelectedRecord(record);
    setIsSelectRecordOpen(false);
    setIsResultOpen(true);
    testMutation.mutate({ recordId: record.id, prompt: promptText });
  };

  const recordSelectionContent = (
    <div className="space-y-4 h-full flex flex-col w-full">
      {/* Поиск */}
      <div className="space-y-2 flex-shrink-0">
        <Label htmlFor="search">{t('templates.test.search_label')}</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            id="search"
            placeholder={t('templates.test.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Список записей */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-2">
        {recordsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="animate-spin mr-2" size={20} />
            {t('templates.test.loading')}
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? t('templates.test.not_found') : t('templates.test.no_records')}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRecords.map((record) => (
              <div key={record.id} onClick={() => handleRecordSelect(record)} className="cursor-pointer w-full">
                <RecordCard record={record} disableNavigation={true} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Функция для отображения контента результата тестирования
  const renderResultContent = () => (
    <div className="flex-1 flex flex-col space-y-4 min-h-0 h-full">
      {selectedRecord && (
        <div className="bg-muted p-3 rounded-lg flex-shrink-0">
          <div className="font-medium">{selectedRecord.name}</div>
        </div>
      )}

      <Separator className="flex-shrink-0" />

      <div className="flex-1 flex flex-col space-y-2 min-h-0">
        <Label className="flex-shrink-0">{t('templates.test.result_label')}</Label>
        <div className="border rounded-lg h-[400px] overflow-y-auto p-4 bg-muted flex-1 min-h-0">
          {testMutation.isPending ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
                <Zap
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-500"
                  size={24}
                />
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-lg font-medium text-foreground">{t('templates.test.testing_title')}</h3>
                <p className="text-sm text-muted-foreground">{t('templates.test.processing')}</p>
              </div>

              <p className="text-xs text-muted-foreground animate-pulse">{t('templates.test.wait_msg')}</p>
            </div>
          ) : testResult ? (
            <div className="w-full max-w-none">
              <div className="text-foreground leading-relaxed space-y-4 text-sm sm:text-base">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xl sm:text-2xl font-bold text-brand-600 mb-4 mt-6 first:mt-0 pb-2 border-b border-border">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-lg sm:text-xl font-semibold text-brand-600 mb-3 mt-5 first:mt-0">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-base sm:text-lg font-medium text-brand-600 mb-2 mt-4 first:mt-0">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => <p className="mb-3 text-foreground leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="mb-4 space-y-1 pl-6">{children}</ul>,
                    ol: ({ children }) => <ol className="mb-4 space-y-1 pl-6">{children}</ol>,
                    li: ({ children }) => (
                      <li className="text-foreground leading-relaxed relative">
                        <span className="absolute -left-6 text-brand-500 font-medium">•</span>
                        {children}
                      </li>
                    ),
                    strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                    em: ({ children }) => <em className="italic text-foreground">{children}</em>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-brand-200 pl-4 py-2 my-4 bg-brand-50/50 italic text-foreground">
                        {children}
                      </blockquote>
                    ),
                    code: ({ children }) => (
                      <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => (
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono text-foreground my-4">
                        {children}
                      </pre>
                    ),
                  }}
                >
                  {cleanMarkdownText(testResult)}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground italic bg-muted/30 p-4 rounded-md text-center">
              {t('templates.test.no_result')}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4 flex-shrink-0 border-t">
        <Button onClick={() => setIsResultOpen(false)}>{t('templates.test.close_btn')}</Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Кнопка для открытия тестирования */}
      {isMobile ? (
        <Sheet open={isSelectRecordOpen} onOpenChange={setIsSelectRecordOpen}>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || !promptText.trim()}
              className="w-12 sm:w-auto"
            >
              <Zap size={14} />
              <span className="hidden sm:inline ml-1.5">{t('templates.test.test_btn')}</span>
            </Button>
          </SheetTrigger>

          <SheetContent side="bottom" className="h-[100vh] max-h-[100vh] pt-safe-area-top overflow-hidden p-0">
            <div className="flex flex-col h-full">
              <SheetHeader className="p-6 border-b">
                <SheetTitle className="text-left">{t('templates.test.select_record_title')}</SheetTitle>
              </SheetHeader>
              <div className="flex-1 p-6 min-h-0">{recordSelectionContent}</div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={isSelectRecordOpen} onOpenChange={setIsSelectRecordOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || !promptText.trim()}
              className="w-12 sm:w-auto"
            >
              <Zap size={14} />
              <span className="hidden sm:inline ml-1.5">{t('templates.test.test_btn')}</span>
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-2xl h-[70vh] max-h-[600px] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{t('templates.test.select_record_title')}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-hidden">
              {recordSelectionContent}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Модальное окно с результатом тестирования */}
      {isMobile ? (
        <Sheet open={isResultOpen} onOpenChange={setIsResultOpen}>
          <SheetContent side="bottom" className="h-[100vh] max-h-[100vh] pt-safe-area-top overflow-hidden p-0">
            <div className="flex flex-col h-full">
              <SheetHeader className="p-6 border-b flex-shrink-0">
                <SheetTitle className="text-left">{t('templates.test.result_title')}</SheetTitle>
              </SheetHeader>
              <div className="flex-1 p-6 min-h-0 overflow-hidden">
                {renderResultContent()}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={isResultOpen} onOpenChange={setIsResultOpen}>
          <DialogContent className="max-w-full sm:max-w-4xl max-h-[85vh] sm:max-h-[90vh] flex flex-col m-0 sm:m-6 rounded-none sm:rounded-lg my-safe-area">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{t('templates.test.result_title')}</DialogTitle>
            </DialogHeader>
            {renderResultContent()}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default TemplateTestDialog;
